import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  OutlinedInput,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
  Autocomplete
} from "@mui/material";
import { MdAdd, MdDelete, MdRemove, MdEdit, MdArrowBack } from "react-icons/md";

import DataGridTable from "../components/common/DataGridTable";
import TableLoader from "../components/common/contentloader/TableLoader";

import { useAuth } from "../state/AuthContext";
import { STORAGE_KEYS, PLATFORM, setScopedItem } from "../components/common/Helper";
import { CONST_ROUTE_PATH } from '../route/RouteConstants';

import DebugService from "../services/DebugService";
import InventoryApiService from "../services/InventoryApiService";
import UserTagPermissionsApiService from "../services/UserTagPermissionsApiService";
import RegistrationApiService from "../services/RegistrationApiService"
import APIConstants from "../services/APIConstants";
import { LoadingButton } from "@mui/lab";
import ConfirmModal from "./common/ConfirmModal";

export const UserTagManagement = () => {
  const {selectedPlatform, userDetails } = useAuth();

  const [searchParams] = useSearchParams();
  const urlUserId = searchParams.get("user-id");
  const [targetUserId, setTargetUserId] = useState(urlUserId || "");
  const [targetUser, setTargetUser] = useState(null);

  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");

  const [tags, setTags] = useState([]);
  const [servicePermissions, setServicePermissions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showCustomTagModal, setShowCustomTagModal] = useState(false);
  const [customTagForm, setCustomTagForm] = useState({ key: "", values: [""] });

  const [showEditTagModal, setShowEditTagModal] = useState(false);
  const [editTagData, setEditTagData] = useState(null);
  const [customTagError, setCustomTagError] = useState("");
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    mode: "save",
    tagKey: "",
    tagValues: [],
    permissionId: null,
  });

  const navigate = useNavigate();

  /**
   * @description Fetch inventory tags and user permissions, then merge them
   * Builds a unified tag list with selected values and permission IDs
   * @author Ashish Sharma
   */
  const fetchData = async () => {
    // 1. Fetch inventory tags
    let inventoryApiResponse;

    const dataRequest = {
      type: "ALL_RESOURCES_TAGS",
      userAccountId: selectedAccountId,
    };

    if (selectedPlatform === PLATFORM.AWS) {
      inventoryApiResponse = await InventoryApiService.getFilterTagList(
        APIConstants.ENDPOINT_AWS_FILTER_TAG,
        dataRequest
      );
    } else if (selectedPlatform === PLATFORM.AZURE) {
      inventoryApiResponse = await InventoryApiService.getAzureFilterTagList(
        APIConstants.ENDPOINT_AZURE_FILTER_TAG,
        dataRequest
      );
    }

    const inventoryTags = (inventoryApiResponse?.data || []).map(item => ({
      key: item.Key,
      values: item.Value || [],
    }));

    // 2. Fetch user permissions
    const userId = targetUserId;
    const permissionMap = {};
    const userOnlyTags = [];

    if (userId) {
      const permRes = await UserTagPermissionsApiService.getTagPermissions(
        selectedAccountId,
        userId
      );

      (permRes?.data?.tagPermissions || []).forEach(item => {
        if (!item.tagKey) return;

        permissionMap[item.tagKey] = {
          tagValues: Array.isArray(item.tagValues) ? item.tagValues : [],
          userTagPermissionId: item.userTagPermissionId,
        };

        if (!inventoryTags.some(t => t.key === item.tagKey)) {
          userOnlyTags.push({ key: item.tagKey, values: item.tagValues || [] });
        }
      });

      // Extract service permissions from API response
      const servicePermissionsData = (permRes?.data?.servicePermissions || []);
      setServicePermissions(servicePermissionsData);
    }

    // 3. Merge
    return [...inventoryTags, ...userOnlyTags]
      .map(tag => ({
        ...tag,
        id: tag.key,
        selectedValues: permissionMap[tag.key]?.tagValues || [],
        originalSelectedValues: permissionMap[tag.key]?.tagValues || [],
        userTagPermissionId: permissionMap[tag.key]?.userTagPermissionId || null,
        source: inventoryTags.some(t => t.key === tag.key) ? "Inventory" : "Custom",
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  };

  /**
   * @description Check if a tag has unsaved changes
   * Compares original and current selected values
   * @author Ashish Sharma
   */
  const hasChanges = (tag) => {
    const orig = tag.originalSelectedValues || [];
    const curr = tag.selectedValues || [];
    if (orig.length !== curr.length) return true;
    return orig.some((v) => !curr.includes(v)) || curr.some((v) => !orig.includes(v));
  };

  /**
   * @description Save tag permission changes to the backend
   * Updates selected values and permission ID in state
   * @author Ashish Sharma
   */
  const handleConfirmSave = async () => {
    const { tagKey, tagValues } = confirmModal;
    if (!tagKey || !selectedAccountId) return;

    const userId = targetUserId;
    if (!userId) return;

    setLoading(true);
    try {
      const payload = {
        userId: String(userId),
        userAccountId: String(selectedAccountId),
        updatedBy: String(userDetails.userId),
        tagKey,
        tagValues: tagValues || [],
      };

      const res = await UserTagPermissionsApiService.upsertTagPermissions(payload);

      setTags((prev) =>
        prev.map((t) =>
          t.key === tagKey ? { ...t, selectedValues: tagValues || [], originalSelectedValues: [...(tagValues || [])] } : t
        )
      );

      const newPermissionId = res?.data?.userTagPermissionId;
      if (newPermissionId) {
        setTags((prev) =>
          prev.map((t) =>
            t.key === tagKey ? { ...t, userTagPermissionId: newPermissionId } : t
          )
        );
      }
    } catch (err) {
      DebugService.error("User Tag Management Component: save tag permission failed", err);
    } finally {
      setLoading(false);
      setConfirmModal({ open: false, mode: "save", tagKey: "", tagValues: [], permissionId: null });
    }
  };

  /**
   * @description Delete a tag permission from the backend
   * Resets selected values and permission ID in state
   * @author Ashish Sharma
   */
  const handleConfirmDelete = async () => {
    const { permissionId, tagKey } = confirmModal;
    if (!permissionId) return;

    setLoading(true);
    try {
      await UserTagPermissionsApiService.deleteTagPermission(permissionId);

      setTags((prev) =>
        prev.map((t) =>
          t.key === tagKey ? { ...t, selectedValues: [], originalSelectedValues: [], userTagPermissionId: null } : t
        )
      );
    } catch (err) {
      DebugService.error("User Tag Management Component: delete tag permission failed", err);
    } finally {
      setLoading(false);
      setConfirmModal({ open: false, mode: "save", tagKey: "", tagValues: [], permissionId: null });
    }
  };

  /**
   * @description Update custom tag key input
   * @author Ashish Sharma
   */
  const handleCustomKeyChange = (e) => {
    const newKey = e.target.value;
    setCustomTagForm((prev) => ({ ...prev, key: newKey }));
    const keyExists = tags.some((tag) => tag.key.toLowerCase() === newKey.trim().toLowerCase());
    setCustomTagError(keyExists ? "Tag key already exists" : "");
  };

  /**
   * @description Update a specific custom tag value by index
   * @author Ashish Sharma
   */
  const handleCustomValueChange = (index, value) => {
    setCustomTagForm((prev) => ({
      ...prev,
      values: prev.values.map((v, i) => (i === index ? value : v)),
    }));
  };

  /**
   * @description Add a new empty value field to custom tag form
   * @author Ashish Sharma
   */
  const addCustomValue = () => {
    setCustomTagForm((prev) => ({
      ...prev,
      values: [...prev.values, ""],
    }));
  };

  /**
   * @description Remove a value field from custom tag form by index
   * @author Ashish Sharma
   */
  const removeCustomValue = (index) => {
    setCustomTagForm((prev) => ({
      ...prev,
      values: prev.values.filter((_, i) => i !== index),
    }));
  };

  /**
   * @description Save a new custom tag with key and values
   * Creates or updates tag permission and adds to tag list
   * @author Ashish Sharma
   */
  const handleSaveCustomTag = async () => {
    const key = customTagForm.key.trim();
    const values = customTagForm.values.map(v => v.trim()).filter(Boolean);

    if (!key || values.length === 0 || !selectedAccountId) return;

    const keyExists = tags.some((tag) => tag.key.toLowerCase() === key.toLowerCase());
    if (keyExists) {
      setCustomTagError("Tag key already exists");
      return;
    }

    const userId = targetUserId;
    if (!userId) return;

    setLoading(true);

    try {
      const payload = {
        userId: String(userId),
        userAccountId: String(selectedAccountId),
        updatedBy: String(userDetails.userId),
        tagKey: key,
        tagValues: values,
      };
    
      await UserTagPermissionsApiService.upsertTagPermissions(payload);
    
      const updatedTags = await fetchData();
      setTags(updatedTags);
    
      setShowCustomTagModal(false);
    } catch (err) {
      DebugService.error("User Tag Management Component: save custom tag failed", err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * @description Open edit modal with tag data
   * @author Ashish Sharma
   */
  const handleEditClick = (tagRow) => {
    setEditTagData({
      key: tagRow.key,
      availableValues: tagRow.values || [],
      selectedValues: tagRow.selectedValues || [],
      source: tagRow.source
    });
    setShowEditTagModal(true);
  };

  /**
   * @description Handle save from edit tag modal
   * Opens confirmation modal with updated values
   * @author Ashish Sharma
   */
  const handleSaveEditTag = () => {
    if (!editTagData) return;

    const cleanedValues = (editTagData.selectedValues || [])
    .map(v => v.trim())
    .filter(Boolean);

    setShowEditTagModal(false);
    setConfirmModal({
      open: true,
      mode: "save",
      tagKey: editTagData.key,
      tagValues: editTagData.selectedValues || [],
      permissionId: null,
    });
  };

  // Filter and set accounts based on selected platform
  useEffect(() => {
    const userAccounts = targetUser?.userAccounts ?? [];

    if (!userAccounts.length) {
      setAccounts([]);
      setSelectedAccountId("");
      return;
    }

    const filteredAccountIds = userAccounts
      .filter(acc => acc.platformEnum === selectedPlatform)
      .map(acc => acc.accountId);

    setAccounts(filteredAccountIds);
    
    const defaultAccountId =
      targetUserId && filteredAccountIds.includes(selectedAccountId)
        ? selectedAccountId
        : filteredAccountIds[0] || "";
    setSelectedAccountId(defaultAccountId);
  
  }, [targetUser]);

  // Fetch tags when selected account changes
  useEffect(() => {
    if (!selectedAccountId) return;
  
    setLoading(true);
    fetchData()
      .then(setTags)
      .catch(() => setTags([]))
      .finally(() => setLoading(false));
  }, [selectedAccountId]);

  // Reset custom tag form when modal opens
  useEffect(() => {
    if (showCustomTagModal) {
      setCustomTagForm({ key: "", values: [""] });
    }
  }, [showCustomTagModal]);

  // Fetch target user details when targetUserId changes
  useEffect(() => {
    if (!targetUserId) return;

    const fetchTargetUser = async () => {
      try {
        const res = await RegistrationApiService.userDetails(targetUserId);  // Assume this API method exists
        setTargetUser(res?.data || null);
      } catch (err) {
        DebugService.error("User Tag Management Component: fetch target user failed", err);
      }
    };

    fetchTargetUser();
  }, [targetUserId]);

  const columns = [
    { field: "key", headerName: "Tag Key", width: 200, align: "left", sortable: false  },
{
    field: "values",
    headerName: "Tag Values",
    flex: 1,
    sortable: false,
    align: "center", // center the text in the column
    renderCell: (params) => {
      const selectedValues = params.row.selectedValues || [];

      return (
        <div 
          style={{ cursor: "pointer" }}  // indicate it's clickable
          onClick={() => handleEditClick(params.row)}
        >
          {selectedValues.length > 0 ? selectedValues.join(", ") : "No values selected"}
        </div>
      );
    },
  },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const { userTagPermissionId } = params.row;
        const canDelete = !!userTagPermissionId;

        return (
          <Box className="user-tag-management-actions-cell">
            <IconButton
              size="small"
              aria-label="Edit"
              title="Edit"
              disabled={loading}
              onClick={() => handleEditClick(params.row)}
            >
              <MdEdit
                color={loading ? "#ccc" : "#FF9900"}
                className="user-tag-management-icon"
              />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Delete"
              title="Delete"
              disabled={loading || !canDelete}
              onClick={() =>
                setConfirmModal({
                  open: true,
                  mode: "delete",
                  tagKey: params.row.key,
                  tagValues: [],
                  permissionId: userTagPermissionId,
                })
              }
            >
              <MdDelete
                color={loading || !canDelete ? "#ccc" : "#506C84"}
                className="user-tag-management-icon"
              />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handleServiceToggle = async (service, isEnabled) => {
    setLoading(true);
    try {
      // Get current service permissions
      const currentServices = servicePermissions.flatMap(sp => sp.services || []);
      let updatedServices;

      if (isEnabled) {
        // Add service
        updatedServices = [...new Set([...currentServices, service])];
      } else {
        // Remove service
        updatedServices = currentServices.filter(s => s !== service);
      }

      // Prepare payload for API
      const payload = {
        userId: targetUserId,
        userAccountId: selectedAccountId,
        updatedBy: String(userDetails.userId),
        services: updatedServices,
      };

      // Call API to update service permissions
      await UserTagPermissionsApiService.upsertServicePermissions(payload);
      
      // Refresh data to get updated service permissions
      await fetchData();
    } catch (err) {
      DebugService.error("User Tag Management: Error toggling service permission", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="user-tag-management-container">
      <Typography variant="h4" component="h1" gutterBottom className="user-tag-management-title">
        User Tag Management
      </Typography>

      <Paper elevation={2} className="user-tag-management-account-paper">
        <FormControl fullWidth size="medium">
          <InputLabel id="account-select-label">Select Account</InputLabel>
          <Select
            labelId="account-select-label"
            value={selectedAccountId}
            label="Select Account"
            onChange={(e) => {
              const newId = e.target.value;
              setSelectedAccountId(newId);
            }}
          >
            {accounts.map((acc) => (
              <MenuItem key={acc} value={acc}>
                {acc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Service Permissions Section */}
      <Paper elevation={2} className="user-tag-management-content-paper" style={{ marginTop: '24px' }}>
        <Box className="user-tag-management-header">
          <Typography variant="h6" component="h2">
            Service Permissions
          </Typography>
        </Box>

        <Divider className="user-tag-management-divider" />

        {loading ? (
          <TableLoader />
        ) : (
          <Box style={{ padding: '20px' }}>
            <Grid container spacing={3}>
              {(selectedPlatform === 'AZURE' ? 
                [
                  { name: 'Virtual Machines', value: 'AZURE_VM' },
                  { name: 'VM Scale Sets', value: 'AZURE_VMSS' },
                  { name: 'Azure MySQL', value: 'AZURE_MYSQL' },
                  { name: 'Azure PostgreSQL', value: 'AZURE_POSTGRESQL' },
                  { name: 'Azure SQL', value: 'AZURE_SQL' }
                ] : 
                [
                  { name: 'EC2', value: 'EC2' },
                  { name: 'RDS', value: 'RDS' },
                  { name: 'ASG', value: 'ASG' }
                ]
              ).map(service => {
                // Get all services from servicePermissions to check if this service is enabled
                const allServices = servicePermissions.flatMap(sp => sp.services || []);
                const isEnabled = allServices.includes(service.value);
                
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={service.value}>
                    <Box 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        padding: '12px 16px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        backgroundColor: '#fafafa'
                      }}
                    >
                      <Typography variant="body1" style={{ fontWeight: '500' }}>
                        {service.name}
                      </Typography>
                      <Switch
                        checked={isEnabled}
                        onChange={() => handleServiceToggle(service.value, !isEnabled)}
                        disabled={loading}
                        color="primary"
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </Paper>

      <Paper elevation={2} className="user-tag-management-content-paper" style={{ marginTop: '24px' }}>
        <Box className="user-tag-management-header">
          <Button
            className="user-tag-management-back-btn"
            variant="outlined"
            startIcon={<MdArrowBack />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <LoadingButton
            className="user-tag-management-add-btn"
            variant="contained"
            title="Add Custom Tag"
            disabled={loading || !selectedAccountId}
            onClick={() => setShowCustomTagModal(true)}
            loading={loading}
            loadingPosition="end"
            endIcon={<MdAdd />}
          >
            Add Custom Tag
          </LoadingButton>
        </Box>

        <Divider className="user-tag-management-divider" />

        {loading ? (
          <TableLoader />
        ) : tags.length === 0 ? (
          <Typography color="text.secondary" align="center" className="user-tag-management-empty-message">
            No tags found for this account
          </Typography>
        ) : (
          <DataGridTable
            columns={columns}
            rows={tags}
            pageSizeOptions={[5, 10, 20, 50]}
            disableRowSelectionOnClick
          />
        )}
      </Paper>

      {confirmModal.open && (
        <ConfirmModal
          open={true}
          headingLabel={
            confirmModal.mode === "delete"
              ? "Confirm Deletion"
              : "Confirm Save Changes"
          }
          message={
            confirmModal.mode === "delete"
              ? [
                  <Typography variant="body1" component="div">
                    Are you sure you want to <strong>delete</strong> the permission for tag
                  </Typography>,
                  <Typography variant="h6" color="error" className="user-tag-management-confirm-tag-key">
                    "{confirmModal.tagKey}"
                  </Typography>,
                  <Typography variant="body2" color="text.secondary">
                    This action cannot be undone and will remove all associated values.
                  </Typography>,
                ]
              : [
                  <Typography variant="body1" component="div">
                    Do you want to save the updated values for tag
                  </Typography>,
                  <Typography variant="h6" color="primary" className="user-tag-management-confirm-tag-key">
                    "{confirmModal.tagKey}"
                  </Typography>,
                  <Typography variant="body2">
                    Selected values will be applied immediately.
                  </Typography>,
                ]
          }
          continueBtnLabel={confirmModal.mode === "delete" ? "Yes, Delete" : "Yes, Save"}
          cancelBtnLabel="Cancel"
          continueButtonProps={
            confirmModal.mode === "delete"
              ? { color: "error", startIcon: <MdDelete /> }
              : { color: "primary", startIcon: <MdEdit /> }
          }
          onActionTaken={(confirmed) => {
            if (confirmed) {
              if (confirmModal.mode === "delete") {
                handleConfirmDelete();
              } else {
                handleConfirmSave();
              }
            }
            setConfirmModal({
              open: false,
              mode: "save",
              tagKey: "",
              tagValues: [],
              permissionId: null,
            });
          }}
          loading={loading}
        />
      )}

      <Modal open={showCustomTagModal} onClose={() => setShowCustomTagModal(false)}>
        <Box className="user-tag-management-modal">
          <Typography variant="h5" gutterBottom>
            Add Custom Tag
          </Typography>
          <Divider className="user-tag-management-modal-divider" />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                size="small"
                label="Tag Key"
                value={customTagForm.key}
                onChange={handleCustomKeyChange}
                error={!!customTagError}
                helperText={customTagError}
              />
            </Grid>

            <Grid item xs={12} sm={7}>
              {customTagForm.values.map((val, vIdx) => (
                <Box key={vIdx} className="user-tag-management-value-row">
                  <TextField
                    fullWidth
                    size="small"
                    label={`Value ${vIdx + 1}`}
                    value={val}
                    onChange={(e) => handleCustomValueChange(vIdx, e.target.value)}
                  />
                  {customTagForm.values.length > 1 && (
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => removeCustomValue(vIdx)}
                    >
                      <MdRemove />
                    </IconButton>
                  )}
                </Box>
              ))}

              <Button
                size="small"
                startIcon={<MdAdd />}
                onClick={addCustomValue}
                className="user-tag-management-add-value-btn"
              >
                Add another value
              </Button>
            </Grid>
          </Grid>

          <Box className="user-tag-management-modal-actions">
            <Button
              variant="outlined"
              onClick={() => setShowCustomTagModal(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveCustomTag}
              disabled={loading || !customTagForm.key.trim() || !customTagForm.values.some(v => v.trim() !== "") || !!customTagError}
            >
              Save Tag
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={showEditTagModal} onClose={() => setShowEditTagModal(false)}>
        <Box className="user-tag-management-modal">
          <Box className="add-user-modal-header">
            <Box>
              <Typography variant="h6" className="add-user-modal-title">
                Edit Tag Values
              </Typography>
              <Typography variant="body2" className="add-user-modal-subtitle">
                Update the selected values for this tag.
              </Typography>
            </Box>
            <Box className="add-user-modal-badge edit">
              Edit
            </Box>
          </Box>

          <Divider className="user-tag-management-modal-divider" />

          <Box className="add-user-modal-body">
            <Grid container spacing={2} className="add-user-modal-grid">
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tag Key"
                  value={editTagData?.key || ""}
                  disabled
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo={editTagData?.source === "Custom"}
                  fullWidth
                  size="small"
                  id="edit-tag-values"
                  options={
                  editTagData?.source === "Custom"
                    ? [...new Set(editTagData?.selectedValues || [])]
                    : editTagData?.availableValues || []
                  }
                  value={editTagData?.selectedValues || []}
                  onChange={(_, newValue) => {
                    const cleanedNewValue = newValue.map(v => typeof v === 'string' ? v.trim() : v).filter(Boolean);
                    setEditTagData((prev) => ({
                      ...prev,
                      selectedValues: cleanedNewValue,
                    }));
                  }}
                  filterSelectedOptions
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Tag Values"
                      placeholder={
                        editTagData?.source === "Custom"
                          ? "Type value and press Enter to add"
                          : "Select or search values"
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={1} className="add-user-modal-actions">
            <Grid item xs={12}>
              <Box className="add-user-modal-actions-inner">
                <Button
                  variant="outlined"
                  onClick={() => setShowEditTagModal(false)}
                  disabled={loading}
                  className="add-user-modal-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveEditTag}
                  disabled={loading}
                  className="add-user-modal-save-btn"
                >
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </Box>
  );
};